<template>
  <v-container fluid class="px-0">
    <!-- Header -->
    <v-list flat>
      <v-list-item three-line>
        <v-list-item-avatar>
          <v-icon style="background-color: gainsboro !important; color: black">mdi-email-fast-outline</v-icon>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title>
            <strong style="font-size: 1rem">Email Warmup</strong>
          </v-list-item-title>
          <v-list-item-subtitle>
            Gradually warm up email accounts to build sender reputation
          </v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action>
          <v-btn color="primary" @click="showCreateDialog = true" class="text-none">
            <v-icon left small>mdi-plus</v-icon>
            Add Email Account
          </v-btn>
        </v-list-item-action>
      </v-list-item>
    </v-list>

    <v-divider />

    <!-- Loading State -->
    <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 300px">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <!-- Empty State -->
    <div v-else-if="warmups.length === 0" class="d-flex flex-column align-center justify-center" style="min-height: 400px">
      <v-icon size="80" color="grey lighten-1" class="mb-4">mdi-email-outline</v-icon>
      <h3 class="text--secondary mb-2">No Email Warmup Accounts</h3>
      <p class="text--disabled mb-4">Add an email account to start warming it up</p>
      <v-btn color="primary" @click="showCreateDialog = true" class="text-none">
        <v-icon left small>mdi-plus</v-icon>
        Add Email Account
      </v-btn>
    </div>

    <!-- Warmup Accounts List -->
    <div v-else class="pa-4">
      <!-- Summary Cards -->
      <v-row class="mb-4">
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="pa-4 text-center">
            <div class="text-h5 font-weight-bold primary--text">{{ warmups.length }}</div>
            <div class="text--secondary text-caption">Total Accounts</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="pa-4 text-center">
            <div class="text-h5 font-weight-bold success--text">{{ activeCount }}</div>
            <div class="text--secondary text-caption">Active</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="pa-4 text-center">
            <div class="text-h5 font-weight-bold warning--text">{{ pausedCount }}</div>
            <div class="text--secondary text-caption">Paused</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="pa-4 text-center">
            <div class="text-h5 font-weight-bold info--text">{{ completedCount }}</div>
            <div class="text--secondary text-caption">Completed</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Accounts Grid -->
      <v-row>
        <v-col v-for="warmup in warmups" :key="warmup.id" cols="12" md="6" lg="4">
          <v-card outlined class="warmup-card" :class="{ 'warmup-paused': warmup.status === 'paused' }">
            <!-- Card Header -->
            <v-card-title class="pb-1 d-flex align-center">
              <v-icon left :color="getStatusColor(warmup.status)" small>{{ getStatusIcon(warmup.status) }}</v-icon>
              <span class="text-truncate text-body-2 font-weight-bold" style="max-width: 200px">
                {{ warmup.email_address }}
              </span>
              <v-spacer />
              <v-chip x-small :color="getStatusColor(warmup.status)" dark class="text-uppercase">
                {{ warmup.status }}
              </v-chip>
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on">
                    <v-icon small>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item v-if="warmup.status === 'active'" @click="openSendDialog(warmup)">
                    <v-list-item-icon><v-icon small color="primary">mdi-send</v-icon></v-list-item-icon>
                    <v-list-item-title class="primary--text">Send Warmup Email</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openTestSendDialog(warmup)">
                    <v-list-item-icon><v-icon small color="orange">mdi-flask-outline</v-icon></v-list-item-icon>
                    <v-list-item-title style="color: #F57C00">Test Send (1 email)</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openRecipientsDialog(warmup)">
                    <v-list-item-icon><v-icon small>mdi-account-group</v-icon></v-list-item-icon>
                    <v-list-item-title>Manage Recipients</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                  <v-list-item v-if="warmup.status === 'active'" @click="pauseWarmup(warmup)">
                    <v-list-item-icon><v-icon small>mdi-pause</v-icon></v-list-item-icon>
                    <v-list-item-title>Pause</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="warmup.status === 'paused'" @click="resumeWarmup(warmup)">
                    <v-list-item-icon><v-icon small>mdi-play</v-icon></v-list-item-icon>
                    <v-list-item-title>Resume</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="isSuperAdmin && warmup.status !== 'done'"
                    @click="markAsDone(warmup)"
                  >
                    <v-list-item-icon><v-icon small color="info">mdi-check-circle-outline</v-icon></v-list-item-icon>
                    <v-list-item-title class="info--text">Done Warming</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openEditDialog(warmup)">
                    <v-list-item-icon><v-icon small>mdi-pencil</v-icon></v-list-item-icon>
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="viewLogs(warmup)">
                    <v-list-item-icon><v-icon small>mdi-history</v-icon></v-list-item-icon>
                    <v-list-item-title>View Logs</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="warmup.imap_host" @click="pollReplies(warmup)">
                    <v-list-item-icon><v-icon small color="teal">mdi-email-sync</v-icon></v-list-item-icon>
                    <v-list-item-title class="teal--text">Check Replies / Bounces</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                  <v-list-item @click="confirmDelete(warmup)">
                    <v-list-item-icon><v-icon small color="error">mdi-delete</v-icon></v-list-item-icon>
                    <v-list-item-title class="error--text">Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-card-title>

            <v-card-subtitle class="pb-2">
              <small>{{ warmup.provider || 'SMTP' }} &bull; Started {{ formatDate(warmup.started_at) }}</small>
            </v-card-subtitle>

            <v-card-text class="pt-0">
              <!-- Recipients Preview -->
              <div v-if="warmup.recipients && warmup.recipients.length > 0" class="mb-3">
                <small class="text--secondary d-block mb-1">Recipients ({{ warmup.recipients.length }})</small>
                <div class="d-flex flex-wrap" style="gap: 4px">
                  <v-chip
                    v-for="(r, idx) in warmup.recipients.slice(0, 3)"
                    :key="idx"
                    x-small
                    outlined
                  >
                    <v-icon x-small left>{{ r.user_id ? 'mdi-account' : 'mdi-email-outline' }}</v-icon>
                    {{ r.name || r.email }}
                  </v-chip>
                  <v-chip v-if="warmup.recipients.length > 3" x-small outlined>
                    +{{ warmup.recipients.length - 3 }} more
                  </v-chip>
                </div>
              </div>
              <div v-else class="mb-3">
                <small class="text--disabled">No recipients configured</small>
              </div>

              <!-- Progress -->
              <div v-if="warmup.status !== 'done'" class="mb-3">
                <div class="d-flex justify-space-between mb-1">
                  <small class="text--secondary">Warmup Progress</small>
                  <small class="font-weight-bold">{{ warmup.days_completed }}/{{ warmup.warmup_duration_days }} days</small>
                </div>
                <v-progress-linear
                  :value="getProgress(warmup)"
                  :color="getStatusColor(warmup.status)"
                  height="8"
                  rounded
                />
              </div>

              <!-- Stats Grid -->
              <v-row dense>
                <v-col cols="6">
                  <div class="stat-box pa-2 rounded">
                    <div class="text-caption text--secondary">Total Sent</div>
                    <div class="font-weight-bold">{{ warmup.total_sent }}</div>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="stat-box pa-2 rounded">
                    <div class="text-caption text--secondary">Sent Today</div>
                    <div class="font-weight-bold">{{ warmup._sent_today || 0 }}<span class="text-caption text--secondary">/{{ warmup.current_daily_limit }}</span></div>
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="stat-box pa-2 rounded">
                    <div class="text-caption text--secondary">Bounces</div>
                    <div class="font-weight-bold" :class="warmup.bounce_count > 0 ? 'error--text' : ''">
                      {{ warmup.bounce_count }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="stat-box pa-2 rounded">
                    <div class="text-caption text--secondary">Failed</div>
                    <div class="font-weight-bold" :class="warmup._failed_count > 0 ? 'warning--text' : ''">
                      {{ warmup._failed_count || 0 }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="stat-box pa-2 rounded">
                    <div class="text-caption text--secondary">Spam</div>
                    <div class="font-weight-bold" :class="warmup.spam_count > 0 ? 'deep-orange--text' : ''">
                      {{ warmup.spam_count }}
                    </div>
                  </div>
                </v-col>
              </v-row>

              <!-- Delivery Rate -->
              <div v-if="warmup.total_sent > 0" class="mt-3">
                <div class="d-flex justify-space-between align-center">
                  <small class="text--secondary">Delivery Rate</small>
                  <small class="font-weight-bold" :class="getDeliveryRateColor(warmup)">
                    {{ getDeliveryRate(warmup) }}%
                  </small>
                </div>
                <v-progress-linear
                  :value="getDeliveryRate(warmup)"
                  :color="getDeliveryRate(warmup) >= 90 ? 'success' : getDeliveryRate(warmup) >= 70 ? 'warning' : 'error'"
                  height="4"
                  rounded
                  class="mt-1"
                />
              </div>

              <!-- Reputation Score -->
              <div class="mt-3">
                <div class="d-flex justify-space-between align-center">
                  <small class="text--secondary">Reputation Score</small>
                  <v-chip
                    x-small
                    :color="getReputationColor(warmup.reputation_score)"
                    dark
                  >
                    {{ warmup.reputation_score }}%
                  </v-chip>
                </div>
                <v-progress-linear
                  :value="warmup.reputation_score"
                  :color="getReputationColor(warmup.reputation_score)"
                  height="4"
                  rounded
                  class="mt-1"
                />
              </div>

              <!-- Quick Send Button -->
              <v-btn
                v-if="warmup.status === 'active' && warmup.recipients && warmup.recipients.length > 0"
                color="primary"
                block
                small
                outlined
                class="mt-3 text-none"
                @click="openSendDialog(warmup)"
              >
                <v-icon left small>mdi-send</v-icon>
                Send Warmup Email
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="600" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left>mdi-email-plus-outline</v-icon>
          {{ editingWarmup ? 'Edit Email Account' : 'Add Email Account for Warmup' }}
          <v-spacer />
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pt-4">
          <v-form ref="warmupForm" v-model="formValid">
            <!-- Email Address -->
            <v-text-field
              v-model="form.email_address"
              label="Email Address"
              outlined
              dense
              prepend-inner-icon="mdi-email-outline"
              :rules="[rules.required, rules.email]"
              @blur="autoFillSmtpUsername"
            />

            <!-- Provider -->
            <v-select
              v-model="form.provider"
              :items="providers"
              label="Email Provider"
              outlined
              dense
              prepend-inner-icon="mdi-server"
              @change="applyProviderDefaults"
            />

            <!-- SMTP Settings -->
            <v-expansion-panels flat class="mb-4">
              <v-expansion-panel>
                <v-expansion-panel-header class="text-body-2 font-weight-bold">
                  <v-icon left small>mdi-cog-outline</v-icon>
                  SMTP / IMAP Settings
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense>
                    <v-col cols="8">
                      <v-text-field v-model="form.smtp_host" label="SMTP Host" outlined dense />
                    </v-col>
                    <v-col cols="4">
                      <v-text-field v-model="form.smtp_port" label="Port" outlined dense type="number" />
                    </v-col>
                  </v-row>
                  <v-text-field
                    v-model="form.smtp_username"
                    label="SMTP Username"
                    outlined dense
                    hint="Must be your full email address (e.g. you@gmail.com)"
                    persistent-hint
                  />
                  <!-- Creating: single password field -->
                  <v-text-field
                    v-if="!editingWarmup"
                    v-model="form.smtp_password"
                    label="SMTP Password"
                    outlined
                    dense
                    :type="showPassword ? 'text' : 'password'"
                    :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    @click:append="showPassword = !showPassword"
                  />
                  <!-- Editing: new + confirm fields (leave blank to keep current) -->
                  <template v-if="editingWarmup">
                    <v-text-field
                      v-model="form.new_smtp_password"
                      label="New Password (leave blank to keep current)"
                      outlined
                      dense
                      :type="showPassword ? 'text' : 'password'"
                      :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                      @click:append="showPassword = !showPassword"
                      :error-messages="form.new_smtp_password && form.confirm_smtp_password && form.new_smtp_password !== form.confirm_smtp_password ? ['Passwords do not match'] : []"
                    />
                    <v-text-field
                      v-if="form.new_smtp_password"
                      v-model="form.confirm_smtp_password"
                      label="Confirm New Password"
                      outlined
                      dense
                      :type="showPassword ? 'text' : 'password'"
                      :error-messages="form.confirm_smtp_password && form.new_smtp_password !== form.confirm_smtp_password ? ['Passwords do not match'] : []"
                    />
                  </template>
                  <v-select
                    v-model="form.encryption"
                    :items="['tls', 'ssl', 'none']"
                    label="Encryption"
                    outlined
                    dense
                  />
                  <v-row dense>
                    <v-col cols="8">
                      <v-text-field v-model="form.imap_host" label="IMAP Host" outlined dense />
                    </v-col>
                    <v-col cols="4">
                      <v-text-field v-model="form.imap_port" label="Port" outlined dense type="number" />
                    </v-col>
                  </v-row>
                  <v-text-field
                    v-model="form.imap_username"
                    label="IMAP Username (optional)"
                    outlined dense
                    hint="Leave blank to use SMTP username. Set if IMAP login differs."
                    persistent-hint
                    class="mb-2"
                  />
                  <v-btn
                    small
                    outlined
                    color="teal"
                    class="text-none mb-2"
                    :loading="testingImap"
                    :disabled="!form.imap_host || !(form.imap_username || form.smtp_username) || !(form.smtp_password || form.new_smtp_password || editingWarmup)"
                    @click="testImapConnection"
                  >
                    <v-icon left small>mdi-lan-check</v-icon>
                    Test IMAP Connection
                  </v-btn>
                  <v-alert v-if="imapTestResult" :type="imapTestResult.success ? 'success' : 'error'" dense outlined class="text-body-2 mt-1">
                    {{ imapTestResult.message }}
                    <div v-if="imapTestResult.folders && imapTestResult.folders.length" class="mt-1">
                      <small>Folders: {{ imapTestResult.folders.join(', ') }}</small>
                    </div>
                  </v-alert>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- Warmup Settings -->
            <div class="text-body-2 font-weight-bold mb-3">
              <v-icon left small>mdi-fire</v-icon>
              Warmup Configuration
            </div>

            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  v-model.number="form.daily_limit"
                  label="Initial Daily Limit"
                  outlined
                  dense
                  type="number"
                  hint="Emails per day to start"
                  persistent-hint
                  :rules="[rules.required, rules.positiveNumber]"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.number="form.max_daily_limit"
                  label="Max Daily Limit"
                  outlined
                  dense
                  type="number"
                  hint="Maximum emails per day"
                  persistent-hint
                  :rules="[rules.required, rules.positiveNumber]"
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  v-model.number="form.ramp_up_increment"
                  label="Daily Ramp-up"
                  outlined
                  dense
                  type="number"
                  hint="Increase limit by this each day"
                  persistent-hint
                  :rules="[rules.required, rules.positiveNumber]"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.number="form.warmup_duration_days"
                  label="Warmup Duration (days)"
                  outlined
                  dense
                  type="number"
                  hint="Total warmup period"
                  persistent-hint
                  :rules="[rules.required, rules.positiveNumber]"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text @click="closeDialog" class="text-none">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="saveWarmup"
            :loading="saving"
            :disabled="!formValid"
            class="text-none"
          >
            {{ editingWarmup ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Recipients Dialog -->
    <v-dialog v-model="showRecipientsDialog" max-width="650" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left>mdi-account-group</v-icon>
          Manage Recipients - {{ recipientsWarmup ? recipientsWarmup.email_address : '' }}
          <v-spacer />
          <v-btn icon @click="showRecipientsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pt-4">
          <!-- Select System Users -->
          <div class="text-body-2 font-weight-bold mb-2">
            <v-icon left small>mdi-account-multiple</v-icon>
            Select Users
          </div>
          <v-autocomplete
            v-model="recipientForm.selectedUserIds"
            :items="userList"
            item-text="name"
            item-value="id"
            label="Search and select users"
            outlined
            dense
            multiple
            chips
            small-chips
            deletable-chips
            clearable
            hide-selected
            :filter="filterUsers"
          >
            <template v-slot:item="{ item, on, attrs }">
              <v-list-item v-on="on" v-bind="attrs">
                <v-list-item-action>
                  <v-checkbox :input-value="recipientForm.selectedUserIds.includes(item.id)" />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
            <template v-slot:selection="{ item }">
              <v-chip small close color="primary" outlined class="ma-1" @click:close="removeSelectedUser(item.id)">
                <v-icon left x-small>mdi-account</v-icon>
                {{ item.name }}
              </v-chip>
            </template>
          </v-autocomplete>

          <v-divider class="my-3" />

          <!-- Manual Email Input -->
          <div class="text-body-2 font-weight-bold mb-2">
            <v-icon left small>mdi-email-edit-outline</v-icon>
            Add External Emails
          </div>
          <div class="d-flex align-center mb-2" style="gap: 8px">
            <v-text-field
              v-model="manualEmailInput"
              label="Enter email address"
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-email-outline"
              @keyup.enter="addManualEmail"
              :error="manualEmailError"
            />
            <v-btn color="primary" @click="addManualEmail" :disabled="!manualEmailInput" small class="text-none">
              <v-icon small>mdi-plus</v-icon>
              Add
            </v-btn>
          </div>
          <small v-if="manualEmailError" class="error--text">Please enter a valid email address</small>

          <!-- Manual Email Chips -->
          <div v-if="recipientForm.manualEmails.length > 0" class="d-flex flex-wrap mt-2" style="gap: 6px">
            <v-chip
              v-for="(email, idx) in recipientForm.manualEmails"
              :key="'manual-' + idx"
              small
              close
              @click:close="removeManualEmail(idx)"
            >
              <v-icon small left>mdi-email-outline</v-icon>
              {{ email }}
            </v-chip>
          </div>

          <!-- Column mapping (shown after file is parsed) -->
          <div v-if="importColumns.length > 0" class="mt-3">
            <v-divider class="mb-3" />
            <div class="text-body-2 font-weight-bold mb-2">
              <v-icon left small>mdi-file-import-outline</v-icon>
              Map Email Column
            </div>
            <v-select
              v-model="importEmailColumn"
              :items="importColumns"
              label="Select the Email column"
              outlined
              dense
              hide-details
              prepend-inner-icon="mdi-table-column"
              class="mb-2"
            />
            <v-btn
              small
              color="primary"
              outlined
              class="text-none"
              :disabled="!importEmailColumn"
              @click="confirmImport"
            >
              <v-icon left small>mdi-plus-circle-outline</v-icon>
              Add {{ importPreviewCount }} email{{ importPreviewCount !== 1 ? 's' : '' }} as recipients
            </v-btn>
            <v-alert v-if="importPreviewCount === 0 && importEmailColumn" type="warning" dense outlined class="mt-2 text-body-2">
              No valid emails found in the selected column.
            </v-alert>
          </div>

          <!-- Summary -->
          <v-alert type="info" dense outlined class="mt-4 text-body-2">
            <strong>{{ totalRecipientCount }}</strong> recipient{{ totalRecipientCount !== 1 ? 's' : '' }} will receive warmup emails
          </v-alert>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <!-- Hidden file input triggered by Import button -->
          <input
            ref="importFileInput"
            type="file"
            accept=".xlsx,.xls,.csv"
            style="display:none"
            @change="onImportFileChange"
          />
          <v-btn outlined color="primary" @click="$refs.importFileInput.click()" class="text-none">
            <v-icon left small>mdi-file-import-outline</v-icon>
            Import
          </v-btn>
          <v-spacer />
          <v-btn text @click="showRecipientsDialog = false" class="text-none">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="saveRecipients"
            :loading="savingRecipients"
            class="text-none"
          >
            Save Recipients
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Send Warmup Email Dialog -->
    <v-dialog v-model="showSendDialog" max-width="550" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="primary">mdi-send</v-icon>
          Send Warmup Email
          <v-spacer />
          <v-btn icon @click="showSendDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pt-4">
          <div v-if="sendWarmup" class="mb-3">
            <v-chip small outlined class="mr-2">
              <v-icon small left>mdi-email-outline</v-icon>
              From: {{ sendWarmup.email_address }}
            </v-chip>
            <v-chip small outlined>
              <v-icon small left>mdi-account-group</v-icon>
              {{ sendWarmup.recipients ? sendWarmup.recipients.length : 0 }} recipient(s)
            </v-chip>
          </div>

          <!-- Recipients Preview -->
          <div v-if="sendWarmup && sendWarmup.recipients && sendWarmup.recipients.length > 0" class="mb-4">
            <small class="text--secondary d-block mb-1">Sending to:</small>
            <div class="d-flex flex-wrap" style="gap: 4px">
              <v-chip
                v-for="(r, idx) in sendWarmup.recipients"
                :key="'send-r-' + idx"
                x-small
                outlined
              >
                {{ r.name || r.email }}
              </v-chip>
            </div>
          </div>

          <v-alert
            v-if="!sendWarmup || !sendWarmup.recipients || sendWarmup.recipients.length === 0"
            type="warning"
            dense
            outlined
            class="text-body-2"
          >
            No recipients configured. Please add recipients first.
          </v-alert>

          <!-- Saved Templates -->
          <div class="mb-3 d-flex align-center" style="gap: 8px">
            <v-select
              :items="emailTemplates"
              item-text="name"
              item-value="id"
              label="Load a saved template"
              outlined
              dense
              hide-details
              clearable
              :loading="loadingTemplates"
              :disabled="loadingTemplates || emailTemplates.length === 0"
              :placeholder="emailTemplates.length === 0 ? 'No saved templates yet' : 'Select a template to load'"
              prepend-inner-icon="mdi-text-box-multiple-outline"
              style="flex: 1"
              @change="loadEmailTemplate($event)"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="item.subject" class="text-caption">{{ item.subject }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action @click.stop>
                  <v-btn icon x-small color="red lighten-1" @click.stop="deleteEmailTemplate(emailTemplates.indexOf(item))">
                    <v-icon x-small>mdi-delete-outline</v-icon>
                  </v-btn>
                </v-list-item-action>
              </template>
            </v-select>
            <v-btn
              small outlined color="primary" class="text-none flex-shrink-0"
              :disabled="!sendForm.subject && !sendForm.body"
              @click="showSaveTemplateInput = !showSaveTemplateInput"
            >
              <v-icon left small>mdi-content-save-outline</v-icon>
              Save
            </v-btn>
          </div>

          <!-- Save template name input -->
          <div v-if="showSaveTemplateInput" class="d-flex align-center mb-3" style="gap: 8px">
            <v-text-field
              v-model="newTemplateName"
              label="Template name"
              outlined dense hide-details
              @keyup.enter="saveEmailTemplate"
              placeholder="e.g. Introduction email"
            />
            <v-btn small color="primary" class="text-none" :disabled="!newTemplateName" @click="saveEmailTemplate">
              Save
            </v-btn>
            <v-btn small text class="text-none" @click="showSaveTemplateInput = false; newTemplateName = ''">
              Cancel
            </v-btn>
          </div>

          <v-divider class="mb-3" />

          <!-- Custom Subject (optional) -->
          <v-text-field
            v-model="sendForm.subject"
            label="Subject (optional - leave blank for random)"
            outlined
            dense
            prepend-inner-icon="mdi-format-title"
            hint="Leave empty to use a randomly generated friendly subject line"
            persistent-hint
          />

          <!-- Custom Body (optional) -->
          <div class="mt-2">
            <label class="v-label theme--light text-body-2 grey--text text--darken-1 mb-1 d-block">
              Email Body (optional — leave blank for default)
            </label>
            <wysiwyg v-model="sendForm.body" />
            <div class="v-messages theme--light mt-1">
              <div class="v-messages__message text-caption grey--text">Leave empty to use a default friendly warmup message</div>
            </div>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text @click="showSendDialog = false" class="text-none">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="sendWarmupEmail"
            :loading="sending"
            :disabled="!sendWarmup || !sendWarmup.recipients || sendWarmup.recipients.length === 0"
            class="text-none"
          >
            <v-icon left small>mdi-send</v-icon>
            Send Now
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Test Send Dialog -->
    <v-dialog v-model="showTestSendDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="orange">mdi-flask-outline</v-icon>
          Test Send
          <v-chip x-small color="orange" dark class="ml-2">1 email only</v-chip>
          <v-spacer />
          <v-btn icon @click="showTestSendDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pt-4">
          <v-alert type="info" dense outlined class="mb-4 text-body-2">
            Sends a single warmup email to the address below — does <strong>not</strong> count toward your daily limit or affect real recipients.
          </v-alert>

          <div v-if="testSendWarmup" class="mb-3">
            <v-chip small outlined>
              <v-icon small left>mdi-email-outline</v-icon>
              From: {{ testSendWarmup.email_address }}
            </v-chip>
          </div>

          <v-text-field
            v-model="testSendEmail"
            label="Send test to"
            outlined
            dense
            prepend-inner-icon="mdi-email-outline"
            placeholder="test@example.com"
            :error-messages="testSendEmailError ? 'Enter a valid email address' : ''"
            @keyup.enter="runTestSend"
          />

          <v-text-field
            v-model="testSendForm.subject"
            label="Subject (optional)"
            outlined
            dense
            prepend-inner-icon="mdi-format-title"
            hint="Leave blank for a random friendly subject"
            persistent-hint
            class="mt-1"
          />

          <div class="mt-2">
            <label class="v-label theme--light text-body-2 grey--text text--darken-1 mb-1 d-block">
              Body (optional — leave blank for default)
            </label>
            <wysiwyg v-model="testSendForm.body" />
            <div class="v-messages theme--light mt-1">
              <div class="v-messages__message text-caption grey--text">Leave blank for the default warmup message</div>
            </div>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text @click="showTestSendDialog = false" class="text-none">Cancel</v-btn>
          <v-btn
            color="orange darken-1"
            dark
            @click="runTestSend"
            :loading="testSending"
            :disabled="!testSendEmail"
            class="text-none"
          >
            <v-icon left small>mdi-flask-outline</v-icon>
            Send Test
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Logs Dialog -->
    <v-dialog v-model="showLogsDialog" max-width="800">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left>mdi-history</v-icon>
          Warmup Logs - {{ selectedWarmup ? selectedWarmup.email_address : '' }}
          <v-spacer />
          <v-btn icon @click="showLogsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-0">
          <v-data-table
            :headers="logHeaders"
            :items="warmupLogs"
            :loading="loadingLogs"
            dense
            :items-per-page="15"
            class="elevation-0"
          >
            <template v-slot:[`item.action`]="{ item }">
              <v-chip x-small :color="getLogActionColor(item.action)" dark>
                {{ item.action }}
              </v-chip>
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <v-chip x-small :color="item.status === 'success' ? 'success' : 'error'" dark>
                {{ item.status }}
              </v-chip>
            </template>
            <template v-slot:[`item.created_at`]="{ item }">
              {{ formatDateTime(item.created_at) }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete the warmup for
          <strong>{{ warmupToDelete ? warmupToDelete.email_address : '' }}</strong>?
          This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showDeleteDialog = false" class="text-none">Cancel</v-btn>
          <v-btn color="error" @click="deleteWarmup" :loading="deleting" class="text-none">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { mapGetters } from "vuex";

export default {
  name: "EmailWarmupComponent",

  inject: ['users'],

  data: () => ({
    warmups: [],
    loading: false,
    saving: false,
    deleting: false,
    sending: false,
    savingRecipients: false,
    showCreateDialog: false,
    showLogsDialog: false,
    showDeleteDialog: false,
    showRecipientsDialog: false,
    showSendDialog: false,
    showTestSendDialog: false,
    showPassword: false,
    editingWarmup: null,
    selectedWarmup: null,
    warmupToDelete: null,
    recipientsWarmup: null,
    sendWarmup: null,
    testSendWarmup: null,
    testSendEmail: "",
    testSendEmailError: false,
    testSending: false,
    testSendForm: { subject: "", body: "" },
    testingImap: false,
    imapTestResult: null,
    pollingReplies: false,
    warmupLogs: [],
    loadingLogs: false,
    formValid: false,
    manualEmailInput: "",
    manualEmailError: false,
    importFile: null,
    importRows: [],
    importColumns: [],
    importEmailColumn: null,
    form: {
      email_address: "",
      provider: "gmail",
      smtp_host: "",
      smtp_port: 587,
      smtp_username: "",
      smtp_password: "",
      imap_username: "",
      encryption: "tls",
      imap_host: "",
      imap_port: 993,
      daily_limit: 10,
      max_daily_limit: 100,
      ramp_up_increment: 3,
      warmup_duration_days: 30,
    },
    recipientForm: {
      selectedUserIds: [],
      manualEmails: [],
    },
    sendForm: {
      subject: "",
      body: "",
    },
    emailTemplates: [],
    loadingTemplates: false,
    showSaveTemplateInput: false,
    newTemplateName: "",
    providers: [
      { text: "Gmail", value: "gmail" },
      { text: "Outlook / Office 365", value: "outlook" },
      { text: "Yahoo Mail", value: "yahoo" },
      { text: "Custom SMTP", value: "smtp" },
    ],
    rules: {
      required: (v) => !!v || "This field is required",
      email: (v) => /.+@.+\..+/.test(v) || "Enter a valid email address",
      positiveNumber: (v) => (v && v > 0) || "Must be greater than 0",
    },
    logHeaders: [
      { text: "Action", value: "action", width: 100 },
      { text: "Recipient", value: "recipient_email" },
      { text: "Subject", value: "subject" },
      { text: "Status", value: "status", width: 100 },
      { text: "Date", value: "created_at", width: 160 },
    ],
  }),

  computed: {
    ...mapGetters({
      get_user: "auth/get_user",
    }),
    isSuperAdmin() {
      return this.get_user && this.get_user.role_id === 1;
    },
    activeCount() {
      return this.warmups.filter((w) => w.status === "active").length;
    },
    pausedCount() {
      return this.warmups.filter((w) => w.status === "paused").length;
    },
    completedCount() {
      return this.warmups.filter((w) => w.status === "completed" || w.status === "done").length;
    },
    userList() {
      const u = this.users ? this.users() : [];
      return u.map((user) => ({
        id: user.id,
        name: user.name,
        email: user.email,
      }));
    },
    totalRecipientCount() {
      return this.recipientForm.selectedUserIds.length + this.recipientForm.manualEmails.length;
    },
    importPreviewCount() {
      if (!this.importEmailColumn || this.importRows.length === 0) return 0;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return this.importRows.filter(row => {
        const val = (row[this.importEmailColumn] || '').toString().trim();
        return emailRegex.test(val) && !this.recipientForm.manualEmails.includes(val);
      }).length;
    },
  },

  mounted() {
    this.fetchWarmups();
  },

  methods: {
    async fetchWarmups() {
      this.loading = true;
      try {
        const response = await this.$axios.get("api/email-warmup");
        if (response.data) {
          this.warmups = response.data;
          // Fetch live stats for each warmup to populate sent_today, failed_count, etc.
          this.warmups.forEach((w, idx) => this.fetchStatsForCard(w, idx));
        }
      } catch (err) {
        this.$toast.error("Failed to load email warmup accounts");
      } finally {
        this.loading = false;
      }
    },

    async fetchStatsForCard(warmup, index) {
      try {
        const res = await this.$axios.get(`api/email-warmup/${warmup.id}/stats`);
        if (res.data) {
          // Merge live stats onto the warmup object
          this.$set(this.warmups, index, {
            ...this.warmups[index],
            reputation_score: res.data.reputation_score,
            total_sent: res.data.total_sent,
            bounce_count: res.data.bounce_count,
            spam_count: res.data.spam_count,
            _sent_today: res.data.sent_today,
            _failed_count: res.data.failed_count,
            _delivery_rate: res.data.delivery_rate,
          });
        }
      } catch {
        // stats fetch is non-critical, fail silently
      }
    },

    async saveWarmup() {
      if (!this.$refs.warmupForm.validate()) return;

      this.saving = true;
      try {
        if (this.editingWarmup) {
          const payload = { ...this.form };
          // Only update password if new + confirm both filled and match
          if (payload.new_smtp_password && payload.new_smtp_password === payload.confirm_smtp_password) {
            payload.smtp_password = payload.new_smtp_password;
          } else {
            delete payload.smtp_password;
          }
          delete payload.new_smtp_password;
          delete payload.confirm_smtp_password;
          const response = await this.$axios.post(
            `api/email-warmup/${this.editingWarmup.id}`,
            payload
          );
          if (response.data) {
            const index = this.warmups.findIndex((w) => w.id === this.editingWarmup.id);
            if (index > -1) {
              this.$set(this.warmups, index, response.data);
            }
            this.$toast.success("Email warmup account updated");
          }
        } else {
          const response = await this.$axios.post("api/email-warmup", this.form);
          if (response.data) {
            this.warmups.unshift(response.data);
            this.$toast.success("Email warmup account created");
          }
        }
        this.closeDialog();
      } catch (err) {
        this.$toast.error("Failed to save email warmup account");
      } finally {
        this.saving = false;
      }
    },

    async pauseWarmup(warmup) {
      try {
        const response = await this.$axios.post(`api/email-warmup/${warmup.id}/pause`);
        if (response.data) {
          const index = this.warmups.findIndex((w) => w.id === warmup.id);
          if (index > -1) {
            this.$set(this.warmups, index, { ...this.warmups[index], ...response.data });
          }
          this.$toast.success("Warmup paused");
        }
      } catch (err) {
        this.$toast.error("Failed to pause warmup");
      }
    },

    async resumeWarmup(warmup) {
      try {
        const response = await this.$axios.post(`api/email-warmup/${warmup.id}/resume`);
        if (response.data) {
          const index = this.warmups.findIndex((w) => w.id === warmup.id);
          if (index > -1) {
            this.$set(this.warmups, index, { ...this.warmups[index], ...response.data });
          }
          this.$toast.success("Warmup resumed");
        }
      } catch (err) {
        this.$toast.error("Failed to resume warmup");
      }
    },

    async markAsDone(warmup) {
      try {
        const response = await this.$axios.post(`api/email-warmup/${warmup.id}/done`);
        if (response.data) {
          const index = this.warmups.findIndex((w) => w.id === warmup.id);
          if (index > -1) {
            this.$set(this.warmups, index, { ...this.warmups[index], ...response.data });
          }
          this.$toast.success("Warmup marked as done");
        }
      } catch (err) {
        this.$toast.error("Failed to mark warmup as done");
      }
    },

    async deleteWarmup() {
      if (!this.warmupToDelete) return;

      this.deleting = true;
      try {
        await this.$axios.delete(`api/email-warmup/${this.warmupToDelete.id}`);
        this.warmups = this.warmups.filter((w) => w.id !== this.warmupToDelete.id);
        this.$toast.success("Email warmup account deleted");
        this.showDeleteDialog = false;
        this.warmupToDelete = null;
      } catch (err) {
        this.$toast.error("Failed to delete warmup account");
      } finally {
        this.deleting = false;
      }
    },

    async viewLogs(warmup) {
      this.selectedWarmup = warmup;
      this.showLogsDialog = true;
      this.loadingLogs = true;
      try {
        const response = await this.$axios.get(`api/email-warmup/${warmup.id}/logs`);
        if (response.data && response.data.data) {
          this.warmupLogs = response.data.data;
        }
      } catch (err) {
        this.$toast.error("Failed to load logs");
      } finally {
        this.loadingLogs = false;
      }
    },

    /*
     * Recipients management
     */
    openRecipientsDialog(warmup) {
      this.recipientsWarmup = warmup;
      const existing = warmup.recipients || [];
      this.recipientForm.selectedUserIds = existing
        .filter((r) => r.user_id)
        .map((r) => r.user_id);
      this.recipientForm.manualEmails = existing
        .filter((r) => r.source === "manual")
        .map((r) => r.email);
      this.importFile = null;
      this.importRows = [];
      this.importColumns = [];
      this.importEmailColumn = null;
      this.showRecipientsDialog = true;
    },

    addManualEmail() {
      const email = this.manualEmailInput.trim();
      if (!email || !/.+@.+\..+/.test(email)) {
        this.manualEmailError = true;
        return;
      }
      this.manualEmailError = false;
      if (!this.recipientForm.manualEmails.includes(email)) {
        this.recipientForm.manualEmails.push(email);
      }
      this.manualEmailInput = "";
    },

    removeManualEmail(index) {
      this.recipientForm.manualEmails.splice(index, 1);
    },

    removeSelectedUser(userId) {
      this.recipientForm.selectedUserIds = this.recipientForm.selectedUserIds.filter(
        (id) => id !== userId
      );
    },

    filterUsers(item, queryText) {
      const search = queryText.toLowerCase();
      return (
        item.name.toLowerCase().includes(search) ||
        item.email.toLowerCase().includes(search)
      );
    },

    async saveRecipients() {
      if (!this.recipientsWarmup) return;

      this.savingRecipients = true;
      try {
        const response = await this.$axios.post(
          `api/email-warmup/${this.recipientsWarmup.id}/recipients`,
          {
            user_ids: this.recipientForm.selectedUserIds,
            emails: this.recipientForm.manualEmails,
          }
        );
        if (response.data) {
          const index = this.warmups.findIndex((w) => w.id === this.recipientsWarmup.id);
          if (index > -1) {
            this.$set(this.warmups, index, response.data);
          }
          this.$toast.success("Recipients updated");
        }
        this.showRecipientsDialog = false;
      } catch (err) {
        this.$toast.error("Failed to update recipients");
      } finally {
        this.savingRecipients = false;
      }
    },

    /*
     * Send warmup email
     */
    openSendDialog(warmup) {
      this.sendWarmup = warmup;
      this.sendForm = { subject: "", body: "" };
      this.showSaveTemplateInput = false;
      this.newTemplateName = "";
      this.showSendDialog = true;
      this.fetchEmailTemplates(warmup.id);
    },

    openTestSendDialog(warmup) {
      this.testSendWarmup = warmup;
      this.testSendEmail = "";
      this.testSendEmailError = false;
      this.testSendForm = { subject: "", body: "" };
      this.showTestSendDialog = true;
    },

    async runTestSend() {
      // simple client-side email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.testSendEmail)) {
        this.testSendEmailError = true;
        return;
      }
      this.testSendEmailError = false;
      this.testSending = true;
      try {
        const response = await this.$axios.post(
          `api/email-warmup/${this.testSendWarmup.id}/test-send`,
          {
            test_email: this.testSendEmail,
            subject: this.testSendForm.subject || null,
            body: this.testSendForm.body || null,
          }
        );
        if (response.data?.response === "success") {
          this.$toast.success(response.data.message || `Test email sent to ${this.testSendEmail}`);
          this.showTestSendDialog = false;
        } else {
          this.$toast.error(response.data?.message || "Test send failed");
        }
      } catch (e) {
        this.$toast.error("Failed to send test email");
      } finally {
        this.testSending = false;
      }
    },

    async fetchEmailTemplates(warmupId) {
      this.loadingTemplates = true;
      try {
        const res = await this.$axios.get(`api/email-warmup/${warmupId}/templates`);
        this.emailTemplates = res.data.data || [];
      } catch (e) {
        this.emailTemplates = [];
      } finally {
        this.loadingTemplates = false;
      }
    },

    async saveEmailTemplate() {
      if (!this.newTemplateName || !this.sendWarmup) return;
      try {
        const res = await this.$axios.post(`api/email-warmup/${this.sendWarmup.id}/templates`, {
          name: this.newTemplateName,
          subject: this.sendForm.subject,
          body: this.sendForm.body,
        });
        if (res.data.response) {
          this.emailTemplates.unshift(res.data.data);
          this.$toast.success(`Template "${this.newTemplateName}" saved`);
          this.newTemplateName = "";
          this.showSaveTemplateInput = false;
        }
      } catch (e) {
        this.$toast.error('Failed to save template');
      }
    },

    loadEmailTemplate(id) {
      if (!id) return;
      const tpl = this.emailTemplates.find(t => t.id === id);
      if (!tpl) return;
      this.sendForm.subject = tpl.subject || "";
      this.sendForm.body = tpl.body || "";
      this.$toast.info(`Template "${tpl.name}" loaded`);
    },

    async deleteEmailTemplate(idx) {
      const tpl = this.emailTemplates[idx];
      if (!tpl || !this.sendWarmup) return;
      try {
        const res = await this.$axios.delete(`api/email-warmup/${this.sendWarmup.id}/templates/${tpl.id}`);
        if (res.data.response) {
          this.emailTemplates.splice(idx, 1);
          this.$toast.success(`Template "${tpl.name}" deleted`);
        }
      } catch (e) {
        this.$toast.error('Failed to delete template');
      }
    },

    async sendWarmupEmail() {
      if (!this.sendWarmup) return;

      this.sending = true;
      try {
        const response = await this.$axios.post(
          `api/email-warmup/${this.sendWarmup.id}/send`,
          {
            subject: this.sendForm.subject || null,
            body: this.sendForm.body || null,
          }
        );
        if (response.data) {
          const sentCount = response.data.sent_count || 0;
          const failedCount = response.data.failed_count || 0;
          const skippedCount = response.data.skipped_count || 0;

          if (response.data.warmup) {
            const index = this.warmups.findIndex((w) => w.id === this.sendWarmup.id);
            if (index > -1) {
              this.$set(this.warmups, index, response.data.warmup);
              // Refresh live stats (reputation, delivery rate, sent today, etc.)
              this.fetchStatsForCard(response.data.warmup, index);
            }
          }

          const skippedNote = skippedCount > 0
            ? ` — ${skippedCount} recipient(s) not reached today (daily limit)`
            : "";

          if (failedCount > 0) {
            this.$toast.warning(`Sent ${sentCount} emails, ${failedCount} failed${skippedNote}`);
          } else {
            this.$toast.success(`Successfully sent ${sentCount} warmup email(s)${skippedNote}`);
          }
        }
        this.showSendDialog = false;
      } catch (err) {
        this.$toast.error(err.response?.data?.message || "Failed to send warmup emails");
      } finally {
        this.sending = false;
      }
    },

    openEditDialog(warmup) {
      this.editingWarmup = warmup;
      this.imapTestResult = null;
      // Correct smtp_username if stored as display name instead of email
      const storedUser = warmup.smtp_username || '';
      const correctedUser = storedUser && /^[^@]+$/.test(storedUser) ? warmup.email_address : storedUser;
      // Correct imap_host if mistakenly set to smtp host
      const storedImap = warmup.imap_host || '';
      const correctedImap = storedImap.startsWith('smtp.') ? storedImap.replace('smtp.', 'imap.') : storedImap;
      this.form = {
        email_address: warmup.email_address,
        provider: warmup.provider || "gmail",
        smtp_host: warmup.smtp_host || "",
        smtp_port: warmup.smtp_port || 587,
        smtp_username: correctedUser || warmup.email_address,
        smtp_password: "",
        new_smtp_password: "",
        confirm_smtp_password: "",
        encryption: warmup.encryption || "tls",
        imap_host: correctedImap,
        imap_port: warmup.imap_port || 993,
        imap_username: warmup.imap_username || "",
        daily_limit: warmup.daily_limit,
        max_daily_limit: warmup.max_daily_limit,
        ramp_up_increment: warmup.ramp_up_increment,
        warmup_duration_days: warmup.warmup_duration_days,
      };
      this.showCreateDialog = true;
    },

    closeDialog() {
      this.showCreateDialog = false;
      this.editingWarmup = null;
      this.imapTestResult = null;
      this.resetForm();
    },

    resetForm() {
      this.form = {
        email_address: "",
        provider: "gmail",
        smtp_host: "",
        smtp_port: 587,
        smtp_username: "",
        smtp_password: "",
        new_smtp_password: "",
        confirm_smtp_password: "",
        encryption: "tls",
        imap_host: "",
        imap_port: 993,
        imap_username: "",
        daily_limit: 10,
        max_daily_limit: 100,
        ramp_up_increment: 3,
        warmup_duration_days: 30,
      };
      this.imapTestResult = null;
      if (this.$refs.warmupForm) {
        this.$refs.warmupForm.resetValidation();
      }
      this.applyProviderDefaults(this.form.provider);
    },

    async testImapConnection() {
      this.testingImap = true;
      this.imapTestResult = null;
      try {
        const url = this.editingWarmup
          ? `api/email-warmup/${this.editingWarmup.id}/test-imap`
          : `api/email-warmup/test-imap`;

        const response = await this.$axios.post(url, {
          imap_host: this.form.imap_host,
          imap_port: this.form.imap_port,
          encryption: this.form.encryption,
          smtp_username: this.form.smtp_username,
          // When editing: use new password if entered, otherwise let backend use stored password
          smtp_password: this.form.smtp_password || this.form.new_smtp_password || undefined,
          imap_username: this.form.imap_username || undefined,
        });

        if (response.data?.response === "success") {
          this.imapTestResult = {
            success: true,
            message: response.data.message,
            folders: response.data.folders || [],
          };
        } else {
          this.imapTestResult = {
            success: false,
            message: response.data?.message || "Connection failed",
          };
        }
      } catch (e) {
        this.imapTestResult = {
          success: false,
          message: e.response?.data?.message || "Failed to connect to IMAP server",
        };
      } finally {
        this.testingImap = false;
      }
    },

    applyProviderDefaults(provider) {
      const defaults = {
        gmail:   { smtp_host: 'smtp.gmail.com',          smtp_port: 587, imap_host: 'imap.gmail.com',              imap_port: 993, encryption: 'tls' },
        outlook: { smtp_host: 'smtp-mail.outlook.com',   smtp_port: 587, imap_host: 'outlook.office365.com',       imap_port: 993, encryption: 'tls' },
        yahoo:   { smtp_host: 'smtp.mail.yahoo.com',     smtp_port: 587, imap_host: 'imap.mail.yahoo.com',         imap_port: 993, encryption: 'ssl' },
      };
      const d = defaults[provider];
      if (d) {
        this.form.smtp_host  = d.smtp_host;
        this.form.smtp_port  = d.smtp_port;
        this.form.imap_host  = d.imap_host;
        this.form.imap_port  = d.imap_port;
        this.form.encryption = d.encryption;
      }
      this.autoFillSmtpUsername();
    },

    autoFillSmtpUsername() {
      // Always use the email address as SMTP username — Google rejects display names
      if (this.form.email_address) {
        this.form.smtp_username = this.form.email_address;
      }
    },

    async pollReplies(warmup) {
      this.pollingReplies = true;
      this.$toast.info("Checking mailbox for replies, bounces & spam...");
      try {
        const response = await this.$axios.post(`api/email-warmup/${warmup.id}/poll-replies`);
        if (response.data?.response === "success") {
          // Update the warmup card with fresh data
          if (response.data.warmup) {
            const index = this.warmups.findIndex((w) => w.id === warmup.id);
            if (index > -1) {
              this.$set(this.warmups, index, response.data.warmup);
              this.fetchStatsForCard(response.data.warmup, index);
            }
          }
          const stats = response.data.stats || {};
          this.$toast.success(
            `Mailbox checked — Replies: ${stats.total_replied || 0}, Bounces: ${stats.bounce_count || 0}, ` +
            `Spam: ${stats.spam_count || 0}, Reputation: ${stats.reputation_score || 0}%`
          );
        } else {
          this.$toast.error(response.data?.message || "Poll failed");
        }
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "Failed to check mailbox");
      } finally {
        this.pollingReplies = false;
      }
    },

    confirmDelete(warmup) {
      this.warmupToDelete = warmup;
      this.showDeleteDialog = true;
    },

    getProgress(warmup) {
      if (!warmup.warmup_duration_days) return 0;
      return Math.min(
        (warmup.days_completed / warmup.warmup_duration_days) * 100,
        100
      );
    },

    getStatusColor(status) {
      const colors = {
        active: "success",
        paused: "warning",
        completed: "info",
        done: "info",
      };
      return colors[status] || "grey";
    },

    getStatusIcon(status) {
      const icons = {
        active: "mdi-play-circle",
        paused: "mdi-pause-circle",
        completed: "mdi-check-circle",
        done: "mdi-check-circle",
      };
      return icons[status] || "mdi-circle";
    },

    getReputationColor(score) {
      if (score >= 80) return "success";
      if (score >= 50) return "warning";
      return "error";
    },

    getDeliveryRate(warmup) {
      if (warmup._delivery_rate !== undefined) return warmup._delivery_rate;
      // fallback: compute from local data
      const total = warmup.total_sent || 0;
      if (total === 0) return 100;
      const bounced = warmup.bounce_count || 0;
      const failed = warmup._failed_count || 0;
      return Math.round(((total - bounced - failed) / total) * 100);
    },

    getDeliveryRateColor(warmup) {
      const rate = this.getDeliveryRate(warmup);
      if (rate >= 90) return "success--text";
      if (rate >= 70) return "warning--text";
      return "error--text";
    },

    getLogActionColor(action) {
      const colors = {
        sent: "primary",
        received: "info",
        replied: "success",
        bounced: "error",
        spam: "deep-orange",
      };
      return colors[action] || "grey";
    },

    formatDate(date) {
      if (!date) return "N/A";
      return this.$date(date).fromNow();
    },

    formatDateTime(date) {
      if (!date) return "N/A";
      return this.$date(date).format(`MMM D, YYYY ${this.$tz.time}`);
    },

    onImportFileChange(event) {
      const file = event.target.files[0];
      // Reset so same file can be re-selected
      event.target.value = '';
      this.parseImportFile(file);
    },

    parseImportFile(file) {
      this.importRows = [];
      this.importColumns = [];
      this.importEmailColumn = null;
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        if (rows.length === 0) {
          this.$toast.warning('The file appears to be empty');
          return;
        }
        this.importRows = rows;
        this.importColumns = Object.keys(rows[0]);
        // Auto-select a column that looks like emails
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const autoCol = this.importColumns.find(col =>
          rows.slice(0, 5).some(r => emailRegex.test((r[col] || '').toString().trim()))
        );
        this.importEmailColumn = autoCol || null;
        this.$toast.info(`File loaded: ${rows.length} rows, ${this.importColumns.length} columns`);
      };
      reader.readAsArrayBuffer(file);
    },

    confirmImport() {
      if (!this.importEmailColumn) return;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      let added = 0;
      this.importRows.forEach(row => {
        const val = (row[this.importEmailColumn] || '').toString().trim();
        if (emailRegex.test(val) && !this.recipientForm.manualEmails.includes(val)) {
          this.recipientForm.manualEmails.push(val);
          added++;
        }
      });
      this.$toast.success(`${added} email${added !== 1 ? 's' : ''} added from sheet`);
      // Reset import state
      this.importFile = null;
      this.importRows = [];
      this.importColumns = [];
      this.importEmailColumn = null;
    },

    exportRecipients() {
      if (!this.recipientsWarmup) return;

      // Map selected user IDs to names/emails
      const allUsers = this.users ? this.users() : [];
      const userRows = this.recipientForm.selectedUserIds.map(uid => {
        const u = allUsers.find(u => u.id === uid);
        return {
          'Type': 'User',
          'Name': u ? u.name : `User ${uid}`,
          'Email': u ? u.email : '',
        };
      });
      const manualRows = this.recipientForm.manualEmails.map(email => ({
        'Type': 'External',
        'Name': '',
        'Email': email,
      }));

      const rows = [...userRows, ...manualRows];
      if (rows.length === 0) {
        this.$toast.info('No recipients to export');
        return;
      }

      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 10 }, { wch: 30 }, { wch: 40 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Recipients');
      const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(
        new Blob([wbOut], { type: 'application/octet-stream' }),
        `recipients-${this.recipientsWarmup.email_address}-${this.$date().format('YYYY-MM-DD')}.xlsx`
      );
      this.$toast.success('Recipients exported');
    },

    exportToExcel() {
      const rows = this.warmups.map(w => ({
        'Email Address': w.email_address,
        'Provider': w.provider || 'SMTP',
        'Status': w.status,
        'Days Completed': w.days_completed,
        'Duration (days)': w.warmup_duration_days,
        'Progress (%)': Math.round((w.days_completed / (w.warmup_duration_days || 1)) * 100),
        'Daily Limit': w.current_daily_limit || w.daily_limit,
        'Max Daily Limit': w.max_daily_limit,
        'Total Sent': w.total_sent || 0,
        'Sent Today': w._sent_today || 0,
        'Bounces': w.bounce_count || 0,
        'Failed': w._failed_count || 0,
        'Spam': w.spam_count || 0,
        'Delivery Rate (%)': this.getDeliveryRate(w),
        'Reputation Score (%)': w.reputation_score || 0,
        'Recipients': (w.recipients || []).length,
        'Started': w.started_at ? this.$date(w.started_at).format('YYYY-MM-DD') : '',
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const colWidths = Object.keys(rows[0] || {}).map(key => ({
        wch: Math.max(key.length, ...rows.map(r => String(r[key] ?? '').length)) + 2,
      }));
      ws['!cols'] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Email Warmup');
      const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(
        new Blob([wbOut], { type: 'application/octet-stream' }),
        `email-warmup-export-${this.$date().format('YYYY-MM-DD')}.xlsx`
      );
      this.$toast.success('Export downloaded');
    },
  },
};
</script>

<style scoped>
.warmup-card {
  transition: all 0.2s ease;
}
.warmup-card:hover {
  border-color: var(--v-primary-base) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.warmup-paused {
  opacity: 0.75;
}
.stat-box {
  background-color: #f5f5f5;
}
.theme--dark .stat-box {
  background-color: #2d2d2d;
}

/* WYSIWYG editor styling */
::v-deep .editr {
  border: 1px solid rgba(0, 0, 0, 0.24);
  border-radius: 4px;
  transition: border-color 0.2s;
}
::v-deep .editr:focus-within {
  border-color: var(--v-primary-base);
}
::v-deep .editr--content {
  min-height: 140px;
  padding: 8px 12px;
  font-size: 14px;
  line-height: 1.6;
  overflow-y: auto;
}
::v-deep .editr--toolbar {
  background: #fafafa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.theme--dark ::v-deep .editr {
  border-color: rgba(255, 255, 255, 0.24);
}
.theme--dark ::v-deep .editr--toolbar {
  background: #333;
  border-bottom-color: rgba(255, 255, 255, 0.12);
}
.theme--dark ::v-deep .editr--content {
  color: #fff;
}
</style>
